/**
 * The row cap of a section on the chart page (Antonin, 19 Oct; lifted inside the chart's own tabs, 19 Oct, `ChartUncappedContext`): a section that lists rows shows its first 5, in the order it
 * already sorts them, and a "Show all N" text button under the list (`ChartShowAll`, chart-row-cap.tsx) opens the rest
 * in place ("Show fewer" closes it). N is what the section holds: where the section fetched only a page (Documents,
 * Notes, Appointments) it is the rows loaded so far, and the section's own paging stays after the list. A list of 5 or
 * fewer draws no button.
 */
import { createContext, useContext, useState } from 'react';

export const CHART_ROW_CAP = 5;

/** Set to true by the tabbed chart: a section in its own tab lists every row it holds (its own paging stays), so no list is
 *  capped and no "Show all N" button is drawn. Outside it (stories, the inner-tab arrangement) the cap holds. */
export const ChartUncappedContext = createContext(false);

export interface RowCap<T> {
  /** The rows to draw: the first 5, or all of them once opened. */
  visible: T[];
  /** Rows held in all. */
  total: number;
  expanded: boolean;
  /** More than 5 rows: the button is drawn. */
  capped: boolean;
  toggle: () => void;
}

export function useRowCap<T>(rows: readonly T[]): RowCap<T> {
  const uncapped = useContext(ChartUncappedContext);
  const [expanded, setExpanded] = useState(false);
  const capped = !uncapped && rows.length > CHART_ROW_CAP;
  return {
    visible: capped && !expanded ? rows.slice(0, CHART_ROW_CAP) : [...rows],
    total: rows.length,
    expanded,
    capped,
    toggle: () => setExpanded((e) => !e),
  };
}
